import { apiRequestErrorFromResponse } from './error-reporting';
import { localDateString } from '#shared';
import type { SeasonGame, SlateTimingOptions } from '#shared';

function apiBaseUrl(): string {
    const runtimeConfig = globalThis as typeof globalThis & {
        __DFL_FRANCHISE_API_BASE_URL__?: string;
        location?: Location;
    };
    const configuredBaseUrl = runtimeConfig.__DFL_FRANCHISE_API_BASE_URL__?.trim();
    if (configuredBaseUrl) {
        return configuredBaseUrl.replace(/\/+$/, '');
    }

    const hostname = runtimeConfig.location?.hostname || '';
    if (hostname === 'dflplaymaker.com' || hostname.endsWith('.dflplaymaker.com') || hostname.endsWith('.amplifyapp.com')) {
        return 'https://api.dflplaymaker.com';
    }

    return 'http://localhost:3001';
}

async function readJson<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw await apiRequestErrorFromResponse(response);
    }

    return response.json() as Promise<T>;
}

/** List stored games, optionally scoped server-side to a season and/or league. */
export async function listScheduledGames(
    filter: { seasonId?: string; leagueId?: string; summary?: boolean } = {},
): Promise<SeasonGame[]> {
    const params = new URLSearchParams();
    if (filter.seasonId) {
        params.set('season', filter.seasonId);
    }
    if (filter.leagueId) {
        params.set('leagueId', filter.leagueId);
    }
    if (filter.summary) {
        params.set('summary', '1');
    }
    const query = params.toString();
    const response = await fetch(`${apiBaseUrl()}/api/list-scheduled-games${query ? `?${query}` : ''}`);
    return readJson<SeasonGame[]>(response);
}
export async function getScheduledGamesByDate(date: Date): Promise<SeasonGame[]> {
    const response = await fetch(`${apiBaseUrl()}/api/list-scheduled-games`);
    const records = await readJson<SeasonGame[]>(response);
    const targetDate = localDateString(date);
    return records.filter((record) => localDateString(new Date(record.date)) === targetDate);
}

/**
 * Backfill `leagueId` onto every stored game that is missing one (games written
 * before the field existed). Idempotent; returns how many were updated.
 */
export async function backfillGameLeagues(): Promise<{ updated: number; total: number }> {
    const response = await fetch(`${apiBaseUrl()}/api/backfill-game-leagues`, {
        method: 'POST',
    });
    return readJson<{ updated: number; total: number }>(response);
}

export async function insertScheduledGame(
    newGame: SeasonGame,
): Promise<boolean> {
    const response = await fetch(`${apiBaseUrl()}/api/scheduled-game`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(newGame),
    });
    if (!response.ok) {
        throw await apiRequestErrorFromResponse(response);
    }
    return true;
}

export type GenerateSeasonScheduleRequest = {
    structure: 'nfl' | 'divisions' | 'round-robin';
    seasonId: string;
    leagueId?: string;
    startDate?: string;
    slateIntervalDays?: number;
    roundsPerSlate?: number;
    playoffStartDate?: string;
    timing?: SlateTimingOptions;
    playoffTiming?: SlateTimingOptions;
    replaceExisting?: boolean;
};

export type GenerateSeasonScheduleResult = {
    structure: 'nfl' | 'divisions' | 'round-robin';
    seasonId: string;
    leagueId: string;
    weeks: number;
    gamesCreated: number;
    gamesDeleted: number;
    games: SeasonGame[];
};

export async function generateSeasonSchedule(
    request: GenerateSeasonScheduleRequest,
): Promise<GenerateSeasonScheduleResult> {
    const response = await fetch(`${apiBaseUrl()}/api/season-schedule`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
    return readJson<GenerateSeasonScheduleResult>(response);
}

export type AdvancePlayoffBracketResult = {
    seasonId: string;
    leagueId?: string;
    resolved: SeasonGame[];
    games: SeasonGame[];
};

/** Fill in any playoff matchups whose feeder games now have results. */
export async function advancePlayoffBracket(
    request: { seasonId: string; leagueId?: string },
): Promise<AdvancePlayoffBracketResult> {
    const response = await fetch(`${apiBaseUrl()}/api/playoff-bracket/advance`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
    return readJson<AdvancePlayoffBracketResult>(response);
}

export async function getScheduledGame(gameId: string): Promise<SeasonGame> {
    const response = await fetch(`${apiBaseUrl()}/api/scheduled-game/${gameId}`, {
        method: 'GET'
    });
    return readJson<SeasonGame>(response);
}

export async function updateScheduledGame(
    updateGame: SeasonGame,
): Promise<boolean> {
    const response = await fetch(`${apiBaseUrl()}/api/scheduled-game`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateGame),
    });

    return response.status === 200 ? true : false
}

export async function deleteScheduledGame(gameId: string): Promise<boolean> {
    const response = await fetch(`${apiBaseUrl()}/api/scheduled-game/${gameId}`, {
        method: 'DELETE'
    });
    return readJson<boolean>(response);
}
